import './styles/graphic.scss';
import { select } from 'd3-selection'; // eslint-disable-line no-unused-vars
import { scaleLinear, scaleTime } from 'd3-scale';
import { line as d3Line, area as d3Area, curveStepAfter } from 'd3-shape';
import { timeParse } from 'd3-time-format';
import { max } from 'd3-array';

const DATA_URL = 'https://ix.cnn.io/data/screwworm-cases/screwworm_cases.json';
const ORANGE = '#F18851';
const GRAY6 = '#b1b1b1'; // axis tick labels — matches reference graphics
const GRAY11 = '#262626';
const FONT = '"CNN Sans Display", "CNN Sans", sans-serif';

const parseDate = timeParse('%Y-%m-%d');
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmtLong = d => `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}`;

// Returns { ticks, fmt } scaled to the data's time span.
// < 60 days  → "June 3" style day labels
// ≥ 60 days  → "June 2026" style month labels
function getAxisConfig(stepData, isMobile) {
  const first = stepData[0].date;
  const last = stepData[stepData.length - 1].date;
  const spanDays = (last - first) / 86400000;

  if (spanDays < 60) {
    const fmt = d => `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}`;
    const midIdx = Math.floor((stepData.length - 1) / 2);
    const ticks = isMobile
      ? [first, last]
      : [first, stepData[midIdx].date, last];
    return { ticks, fmt };
  }

  // Monthly view: one tick per month (first day of each month in range)
  const fmt = d => `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
  const months = [];
  const cursor = new Date(first.getFullYear(), first.getMonth(), 1);
  while (cursor <= last) {
    months.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  // On mobile, thin to first and last month only
  const ticks = isMobile && months.length > 2
    ? [months[0], months[months.length - 1]]
    : months;
  return { ticks, fmt };
}

export default function (props, container) {
  const { initialWidth } = props;
  const isMobile = initialWidth <= 500;

  container.html('<p class="sw-loading">Loading\u2026</p>');

  fetch(DATA_URL)
    .then(r => r.json())
    .then((json) => {
      container.html('');
      renderDashboard(json, container, initialWidth, isMobile);
      if (window.pymChild) window.pymChild.sendHeight();
    })
    .catch(() => {
      container.html('<p class="sw-error">Unable to load data.</p>');
    });
}

function renderDashboard(json, container, width, isMobile) {
  const cases = json.data
    .map(d => ({ ...d, date: parseDate(d.confirmed_date) }))
    .sort((a, b) => a.date - b.date);

  renderDek(json, cases);
  renderStats(json, cases, container);

  const row = container.append('div')
    .attr('class', isMobile ? 'sw-charts-row is-mobile' : 'sw-charts-row');

  // line chart ~58% of width on desktop; full width on mobile
  const lineW = isMobile ? width : Math.round(width * 0.58);
  const specW = isMobile ? width : width - lineW - 24;

  // Compute species chart height so both charts share the same height.
  // Uses a floor of 170px so the line chart never collapses with few species.
  const speciesCount = new Set(cases.map(d => d.species)).size;
  const sharedChartH = Math.max(150, 8 + speciesCount * (28 + 12) - 12 + 8);

  renderCumulativeChart(cases, row.append('div').attr('class', 'sw-chart-cell'), lineW, isMobile, sharedChartH);
  renderSpeciesChart(cases, row.append('div').attr('class', 'sw-chart-cell'), specW, isMobile, sharedChartH);

  renderTable(cases, container);

  // Populate the #footer elements below the graphic (matches rig convention)
  select('#footer .footnote').text(`Note: Data as of ${json.lastUpdatedStr}`);
  select('#footer .source').html(`Source: <a href="${json.sourceUrl}" target="_blank">${json.source}</a>`);
}

// ---------------------------------------------------------------------------
// Stat bar
// ---------------------------------------------------------------------------

const SMALL_NUMS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const numWord = n => (n < 10 ? SMALL_NUMS[n] : n);

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

function renderStats(json, cases, container) {
  const countyCount = new Set(cases.map(d => `${d.county}-${d.state}`)).size;
  const statsData = [
    { value: json.latestValue, label: 'Confirmed cases' },
    { value: json.stateCount, label: json.stateCount === 1 ? 'State' : 'States' },
    { value: countyCount, label: countyCount === 1 ? 'County' : 'Counties' },
  ];

  const bar = container.append('div').attr('class', 'sw-stats');
  statsData.forEach((s, i) => {
    const el = bar.append('div')
      .attr('class', i === 0 ? 'sw-stat sw-stat--primary' : 'sw-stat');
    el.append('div').attr('class', 'sw-stat-value').text(s.value);
    el.append('div').attr('class', 'sw-stat-label').text(s.label);
  });
}

function renderDek(json, cases) {
  const countyCount = new Set(cases.map(d => `${d.county}-${d.state}`)).size;
  const latestDate = fmtLong(cases[cases.length - 1].date);
  const stateWord = json.stateCount === 1 ? 'state' : 'states';
  const countyWord = countyCount === 1 ? 'county' : 'counties';

  const sentence = 'After decades without cases in livestock, the flesh-eating parasite and longtime foe of American ranchers was detected again in June 2026.';

  select('.dek').text(sentence);
}

// ---------------------------------------------------------------------------
// Cumulative cases over time (step line chart)
// ---------------------------------------------------------------------------

function buildCumulativeData(cases) {
  const byDate = {};
  cases.forEach((d) => {
    byDate[d.confirmed_date] = (byDate[d.confirmed_date] || 0) + 1;
  });
  let running = 0;
  return Object.keys(byDate).sort().map((k) => {
    running += byDate[k];
    return { date: parseDate(k), count: running };
  });
}

function renderCumulativeChart(cases, container, width, isMobile, svgHeight) {
  const stepData = buildCumulativeData(cases);
  const last = stepData[stepData.length - 1];

  container.append('p').attr('class', 'sw-chart-title').text('Confirmed cases over time');

  const margin = {
    top: 0, right: 50, bottom: 24, left: 8,
  };
  const svgH = svgHeight;
  const iW = width - margin.left - margin.right;
  const iH = svgH - margin.top - margin.bottom;

  // Extend x domain so the final step line has room to breathe
  const xMax = new Date(last.date.getTime() + 1 * 86400000);
  const xScale = scaleTime().domain([stepData[0].date, xMax]).range([0, iW]);
  const yScale = scaleLinear()
    .domain([0, Math.ceil(max(stepData, d => d.count) * 1.2)])
    .range([iH, 0]);

  const svg = container.append('svg').attr('width', width).attr('height', svgH);
  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  // Zero baseline
  g.append('line')
    .attr('x1', 0).attr('x2', iW).attr('y1', iH)
    .attr('y2', iH)
    .attr('stroke', '#6e6e6e')
    .attr('stroke-width', 0.5)
    .attr('shape-rendering', 'crispEdges');

  // Area fill
  const areaFn = d3Area()
    .x(d => xScale(d.date))
    .y0(iH).y1(d => yScale(d.count))
    .curve(curveStepAfter);
  g.append('path').datum(stepData)
    .attr('fill', ORANGE).attr('fill-opacity', 0.1)
    .attr('d', areaFn);

  // Step line
  const lineFn = d3Line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.count))
    .curve(curveStepAfter);
  g.append('path').datum(stepData)
    .attr('fill', 'none').attr('stroke', ORANGE)
    .attr('stroke-width', 2)
    .attr('d', lineFn);

  // Dots at each step
  g.selectAll('.step-dot').data(stepData).enter()
    .append('circle')
    .attr('cx', d => xScale(d.date))
    .attr('cy', d => yScale(d.count))
    .attr('r', d => (d === last ? 4.5 : 3))
    .attr('fill', ORANGE)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5);

  // Endpoint count label
  g.append('text')
    .attr('x', xScale(last.date) + 9)
    .attr('y', yScale(last.count) + 4)
    .attr('fill', GRAY11)
    .style('font-size', '12px')
    .style('font-weight', '700')
    .style('font-family', FONT)
    .text(`${last.count} cases`);

  // X-axis labels — format and tick count adapt to the data's time span
  const { ticks: tickDates, fmt: fmtAxis } = getAxisConfig(stepData, isMobile);

  g.selectAll('.x-label').data(tickDates).enter()
    .append('text')
    .attr('x', d => xScale(d))
    .attr('y', iH + 18)
    .attr('fill', GRAY6)
    .attr('text-anchor', (d, i) => {
      if (i === 0) return 'start';
      if (i === tickDates.length - 1) return 'end';
      return 'middle';
    })
    .style('font-size', '12px')
    .style('font-family', FONT)
    .text(fmtAxis);
}

// ---------------------------------------------------------------------------
// Cases by species (horizontal bar chart)
// ---------------------------------------------------------------------------

function renderSpeciesChart(cases, container, width, isMobile, svgHeight) { // eslint-disable-line no-unused-vars
  const specMap = {};
  cases.forEach((d) => { specMap[d.species] = (specMap[d.species] || 0) + 1; });
  const data = Object.keys(specMap)
    .map(k => ({ species: k, count: specMap[k] }))
    .sort((a, b) => b.count - a.count);

  container.append('p').attr('class', 'sw-chart-title').text('Cases by animal species');

  const barH = 28;
  const barGap = 8;
  const margin = {
    top: 8, right: 20, bottom: 8, left: 60,
  };
  const iW = width - margin.left - margin.right;
  const svgH = svgHeight;

  // Vertically center the bars when the shared height exceeds the natural content height
  const naturalH = data.length * (barH + barGap) - barGap;
  const topOffset = Math.round((svgH - margin.top - margin.bottom - naturalH) / 2) + margin.top;

  const xScale = scaleLinear()
    .domain([0, max(data, d => d.count) + 0.8])
    .range([0, iW]);

  const svg = container.append('svg').attr('width', width).attr('height', svgH);
  const g = svg.append('g').attr('transform', `translate(${margin.left},${topOffset})`);

  const groups = g.selectAll('.species-group')
    .data(data).enter()
    .append('g')
    .attr('transform', (d, i) => `translate(0,${i * (barH + barGap)})`);

  groups.append('text')
    .attr('x', -8).attr('y', barH / 2 + 4)
    .attr('text-anchor', 'end')
    .attr('fill', GRAY11)
    .style('font-size', '12px')
    .style('font-family', FONT)
    .text(d => d.species);

  groups.append('rect')
    .attr('width', d => xScale(d.count)).attr('height', barH)
    .attr('fill', ORANGE)
    .attr('rx', 2);

  groups.append('text')
    .attr('x', d => xScale(d.count) + 6).attr('y', barH / 2 + 4)
    .attr('fill', GRAY11)
    .style('font-size', '12px')
    .style('font-weight', '700')
    .style('font-family', FONT)
    .text(d => d.count);
}

// ---------------------------------------------------------------------------
// Case log table
// ---------------------------------------------------------------------------

function renderTable(cases, container) {
  const PREVIEW = 5;
  const reversed = [...cases].reverse();
  const hasMore = reversed.length > PREVIEW;

  container.append('p').attr('class', 'sw-chart-title sw-table-title').text('All confirmed cases');

  const wrap = container.append('div').attr('class', 'sw-table-wrap');
  const table = wrap.append('table').attr('class', 'sw-table');

  const thead = table.append('thead').append('tr');
  ['Date', 'County', 'State', 'Animal'].forEach(h => thead.append('th').text(h));

  const tbody = table.append('tbody');
  reversed.forEach((d, i) => {
    const tr = tbody.append('tr');
    if (i >= PREVIEW) tr.classed('sw-table-hidden', true);
    tr.append('td').text(fmtLong(d.date));
    tr.append('td').text(d.county);
    tr.append('td').text(d.state);
    tr.append('td').text(d.species);
  });

  if (hasMore) {
    const btn = container.append('div').attr('class', 'sw-toggle-wrap')
      .append('button')
      .attr('class', 'sw-toggle-btn')
      .text(`Show all ${reversed.length} cases`);

    btn.on('click', () => {
      const expanded = tbody.selectAll('.sw-table-hidden').size() === 0;
      tbody.selectAll('.sw-table-hidden').classed('sw-table-hidden', false);
      if (expanded) {
        tbody.selectAll('tr').filter((d, i) => i >= PREVIEW).classed('sw-table-hidden', true);
        btn.text(`Show all ${reversed.length} cases`);
      } else {
        btn.text('Show fewer');
      }
      if (window.pymChild) window.pymChild.sendHeight();
    });
  }
}
